// frontend/src/utils/offlineStorage.ts
/**
 * Offline storage for the last N calculations using IndexedDB.
 *
 * Strategy: "Recent Calculations (Read-Only Offline)"
 * - When a calculation succeeds online, save a snapshot to IndexedDB.
 * - When offline, the OfflineGuard reads these records for display.
 * - Keeps at most MAX_RECORDS entries; oldest are pruned automatically.
 *
 * This module wraps IndexedDB with a Promise-based API to avoid callback hell.
 */

const DB_NAME = 'lashing-offline-db';
const DB_VERSION = 1;
const STORE_NAME = 'recent-calculations';
const MAX_RECORDS = 5;

export interface OfflineCalculation {
    id: string;
    timestamp: string;         // ISO 8601
    summary: string;           // Human-readable: "28T Ekskavatör / 40' Flatrack / Deniz A"
    inputs: Record<string, unknown>;
    results: Record<string, unknown>;
}

// ── Internal Helpers ──────────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                store.createIndex('timestamp', 'timestamp', { unique: false });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function runTransaction<T>(
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
    return openDB().then(
        (db) =>
            new Promise((resolve, reject) => {
                const tx = db.transaction(STORE_NAME, mode);
                const store = tx.objectStore(STORE_NAME);
                const req = fn(store);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error);
            })
    );
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Save a calculation snapshot to IndexedDB.
 * Automatically prunes the oldest record if MAX_RECORDS is exceeded.
 */
export async function saveCalculationOffline(calc: OfflineCalculation): Promise<void> {
    try {
        const db = await openDB();

        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);

            // Upsert the new record
            const putReq = store.put(calc);
            putReq.onerror = () => reject(putReq.error);

            // After put, check count and prune if needed
            putReq.onsuccess = () => {
                const countReq = store.count();
                countReq.onsuccess = () => {
                    if (countReq.result <= MAX_RECORDS) {
                        resolve();
                        return;
                    }
                    // Fetch all by timestamp asc, delete the oldest
                    const idx = store.index('timestamp');
                    const cursorReq = idx.openCursor(null, 'next');
                    cursorReq.onsuccess = () => {
                        const cursor = cursorReq.result;
                        if (cursor) {
                            store.delete(cursor.primaryKey);
                        }
                        resolve();
                    };
                    cursorReq.onerror = () => resolve(); // non-fatal
                };
                countReq.onerror = () => resolve();
            };

            tx.onerror = () => reject(tx.error);
        });
    } catch (err) {
        // Never throw from this utility — offline storage is best-effort
        console.warn('[offlineStorage] saveCalculationOffline failed:', err);
    }
}

/**
 * Retrieve the most recent calculations, newest first.
 */
export async function getRecentCalculations(): Promise<OfflineCalculation[]> {
    try {
        const db = await openDB();
        return new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const idx = store.index('timestamp');
            const results: OfflineCalculation[] = [];

            // Open cursor in descending order (newest first)
            const cursorReq = idx.openCursor(null, 'prev');
            cursorReq.onsuccess = () => {
                const cursor = cursorReq.result;
                if (cursor && results.length < MAX_RECORDS) {
                    results.push(cursor.value as OfflineCalculation);
                    cursor.continue();
                } else {
                    resolve(results);
                }
            };
            cursorReq.onerror = () => resolve([]);
        });
    } catch (err) {
        console.warn('[offlineStorage] getRecentCalculations failed:', err);
        return [];
    }
}

/**
 * Delete all stored calculations (e.g., on logout).
 */
export async function clearOfflineCalculations(): Promise<void> {
    try {
        await runTransaction('readwrite', (store) => store.clear());
    } catch (err) {
        console.warn('[offlineStorage] clearOfflineCalculations failed:', err);
    }
}
